import PendingAffectationsList from '@/components/Affectations/PendingAffectationsList';
import EtablissementAutocomplete from '@/components/Etablissement/EtablissementAutocomplete';
import {
  useCreateAffectationMutation,
  useGetAffectationCountQuery,
} from '@/features/api/endpoints/affectationEndpoints';
import { useGetRolesQuery } from '@/features/api/endpoints/roleEndpoints';
import OnboardingPageLoader from '@/pages/OnboardingPageLoader';
import { useGetCurrentUser } from '@/shared/auth/useDirectusUser';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Center,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconClock, IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Navigate } from 'react-router-dom';
import { z } from 'zod';

const rowSchema = z.object({
  role: z.number(),
  etablissement: z.string().optional(),
});

const formSchema = z.object({
  affectations: z.array(rowSchema).min(1),
});

type FormValues = z.infer<typeof formSchema>;

const OnboardingPage = () => {
  const { t } = useTranslate();
  const { status: userStatus, user } = useGetCurrentUser();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const userId = user?.id;

  const { data: draftCount, isLoading: loadingCount } = useGetAffectationCountQuery(
    {
      filter: {
        user: { _eq: userId },
        status: { _eq: 'draft' },
      },
    },
    { skip: !userId },
  );

  const { data: publishedCount, isLoading: loadingPublishedCount } = useGetAffectationCountQuery(
    {
      filter: {
        user: { _eq: userId },
        status: { _eq: 'published' },
      },
    },
    { skip: !userId },
  );

  const { data: roles = [], isLoading: loadingRoles } = useGetRolesQuery({
    sort: ['name'],
  });
  const [createAffectation] = useCreateAffectationMutation();

  const {
    control,
    clearErrors,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      affectations: [
        {
          role: undefined as unknown as number,
          etablissement: undefined as unknown as string,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'affectations',
  });

  const affectationsValues = useWatch({
    control,
    name: 'affectations',
  });

  const isEtablissementRequired = (roleId: number | undefined): boolean => {
    if (roleId === undefined) {
      return false;
    }
    return roles.find(role => role.id === roleId)?.etablissementObligatoire === true;
  };

  const onSubmit = async (data: FormValues) => {
    if (!userId) return;
    setSubmitError(null);

    let hasMissingRequiredEtablissement = false;
    clearErrors('affectations');

    data.affectations.forEach((item, index) => {
      if (isEtablissementRequired(item.role) && !item.etablissement?.trim()) {
        hasMissingRequiredEtablissement = true;
        setError(`affectations.${index}.etablissement`, {
          type: 'manual',
          message: 'required',
        });
      }
    });

    if (hasMissingRequiredEtablissement) {
      return;
    }

    try {
      await Promise.all(
        data.affectations.map(item =>
          createAffectation({
            role: item.role,
            etablissement: item.etablissement,
            user: userId,
            status: 'draft',
          }).unwrap(),
        ),
      );
    } catch {
      setSubmitError(t('onboarding.form.error.submit'));
    }
  };

  if (userStatus === 'loading' || loadingCount || loadingPublishedCount || loadingRoles) {
    return <OnboardingPageLoader />;
  }

  if ((publishedCount ?? 0) > 0) {
    return <Navigate to="/" replace />;
  }

  if ((draftCount ?? 0) > 0) {
    return (
      <Center mih="60vh">
        <Stack gap="md" maw={1000} w="100%">
          <Card withBorder radius="md" padding="xl" shadow="sm">
            <Stack gap="md" align="center">
              <ThemeIcon size="xl" radius="xl" variant="light" color="yellow">
                <IconClock size={24} />
              </ThemeIcon>
              <Title order={2} ta="center">
                {t('onboarding.pending.title')}
              </Title>
              <Text c="dimmed" ta="center">
                {t('onboarding.pending.description')}
              </Text>
              {userId && <PendingAffectationsList userId={userId} />}
            </Stack>
          </Card>
        </Stack>
      </Center>
    );
  }

  const roleOptions = roles.map(r => ({ value: String(r.id), label: r.name ?? String(r.id) }));

  return (
    <Center mih="50vh">
      <Stack gap="md">
        <Title order={1}>{t('onboarding.title')}</Title>
        <Text c="dimmed">{t('onboarding.description')}</Text>

        {submitError && (
          <Alert color="red" withCloseButton onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack gap="xs">
            {fields.map((field, index) => {
              const selectedRole = affectationsValues?.[index]?.role;
              const etablissementRequired = isEtablissementRequired(selectedRole);

              return (
                <Grid key={field.id} align="flex-start">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Controller
                      control={control}
                      name={`affectations.${index}.role`}
                      render={({ field: f, fieldState }) => (
                        <Select
                          label={t('onboarding.form.role')}
                          placeholder={t('onboarding.form.role.placeholder')}
                          data={roleOptions}
                          value={f.value ? String(f.value) : null}
                          onChange={val => {
                            const nextRole = val !== null ? Number(val) : undefined;
                            f.onChange(nextRole);

                            if (!isEtablissementRequired(nextRole)) {
                              clearErrors(`affectations.${index}.etablissement`);
                            }
                          }}
                          error={fieldState.error ? t('onboarding.form.error.required') : undefined}
                          required
                        />
                      )}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 7 }}>
                    <Controller
                      control={control}
                      name={`affectations.${index}.etablissement`}
                      render={({ field: f, fieldState }) => (
                        <EtablissementAutocomplete
                          label={t('onboarding.form.etablissement')}
                          placeholder={t('profil.affectations.form.etablissement.placeholder')}
                          value={f.value ?? null}
                          onChange={val => f.onChange(val ?? undefined)}
                          error={
                            fieldState.error
                              ? t('profil.affectations.form.error.required')
                              : undefined
                          }
                          disabled={isSubmitting}
                          required={etablissementRequired}
                        />
                      )}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 1 }}>
                    <Group justify="flex-end" pt="50%">
                      <Tooltip label={t('onboarding.form.removeAffectation')} withArrow>
                        {/* Le span reste actif quand le bouton est désactivé, pour garder l'infobulle. */}
                        <span>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="lg"
                            disabled={fields.length <= 1}
                            onClick={() => remove(index)}
                            aria-label={t('onboarding.form.removeAffectation')}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </span>
                      </Tooltip>
                    </Group>
                  </Grid.Col>
                </Grid>
              );
            })}

            <Group mt="xs">
              <Button
                variant="subtle"
                leftSection={<IconPlus size={16} />}
                onClick={() =>
                  append({
                    role: undefined as unknown as number,
                    etablissement: undefined as unknown as string,
                  })
                }
              >
                {t('onboarding.form.addAffectation')}
              </Button>
            </Group>

            <Button type="submit" loading={isSubmitting} mt="md">
              {t('onboarding.form.submit')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Center>
  );
};

export default OnboardingPage;
